import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, RefreshCw, X } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { cn } from '@/lib/utils';
import {
  DEFAULT_OPS_EMAIL_RECIPIENTS,
  OPS_EMAIL_CHANNEL_LABELS,
  OPS_EMAIL_CHANNELS,
  OPS_EMAIL_RECIPIENTS_KEY,
  emptyOpsEmailRecipient,
  isOpsEmailValid,
  resolveOpsEmailRecipientsFromApi,
  type OpsEmailChannel,
  type OpsEmailRecipient,
} from '@/lib/ops-email-recipients';

interface ConfigRow {
  key: string;
  value: unknown;
  updated_at: string | null;
}

const QUERY_KEY = ['admin', 'ops-email-recipients'] as const;

export function OpsEmailNotificationsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await dbClient.rpc<ConfigRow[]>('admin_get_all_config');
      if (error) throw error;
      const rows = normalizeRpcResponse(data);
      return rows.find((row) => row.key === OPS_EMAIL_RECIPIENTS_KEY)?.value ?? null;
    },
    retry: false,
  });

  const [recipients, setRecipients] = useState<OpsEmailRecipient[]>(
    DEFAULT_OPS_EMAIL_RECIPIENTS.map((item) => ({ ...item }))
  );
  const recipientsRef = useRef(recipients);
  recipientsRef.current = recipients;
  const [newEmail, setNewEmail] = useState('');
  const [newChannels, setNewChannels] = useState({ store: true, licenses: true });
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (isLoading) return;
    if (isError) {
      setRecipients(DEFAULT_OPS_EMAIL_RECIPIENTS.map((item) => ({ ...item })));
      return;
    }
    setRecipients(resolveOpsEmailRecipientsFromApi(data).recipients);
  }, [data, isError, isLoading]);

  const saveMutation = useMutation({
    mutationFn: async (payload: { next: OpsEmailRecipient[]; previous: OpsEmailRecipient[] }) => {
      setRecipients(payload.next);
      const { error } = await dbClient.rpc('admin_upsert_app_config', {
        p_key: OPS_EMAIL_RECIPIENTS_KEY,
        p_value: payload.next,
      });
      if (error) {
        setRecipients(payload.previous);
        throw new Error(error.message);
      }
      return payload.next;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'app-config-all'] });
    },
  });

  const persist = (updater: (prev: OpsEmailRecipient[]) => OpsEmailRecipient[]) => {
    const previous = recipientsRef.current;
    const next = updater(previous);
    recipientsRef.current = next;
    saveMutation.mutate({ next, previous });
  };

  const editable = !saveMutation.isPending && !isLoading;

  const addRecipient = () => {
    const email = newEmail.trim().toLowerCase();
    setFormError(null);
    if (!email) return;
    if (!isOpsEmailValid(email)) {
      setFormError('Ese correo no es válido.');
      return;
    }
    if (recipients.some((item) => item.email === email)) {
      setFormError('Ese correo ya está en la lista.');
      return;
    }
    if (!newChannels.store && !newChannels.licenses) {
      setFormError('Marca al menos un tipo de aviso.');
      return;
    }
    setNewEmail('');
    persist((prev) => [{ ...emptyOpsEmailRecipient(email), ...newChannels }, ...prev]);
  };

  const toggleChannel = (email: string, channel: OpsEmailChannel, checked: boolean) => {
    persist((prev) =>
      prev.map((item) => (item.email === email ? { ...item, [channel]: checked } : item))
    );
  };

  const removeRecipient = (email: string) => {
    persist((prev) => prev.filter((item) => item.email !== email));
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notificaciones de pedidos"
        description="Avisos internos de tienda y licencias. El From es siempre pedidos@aipeces.com; aquí eliges quién los recibe."
      />

      <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Mail size={18} />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-bold text-foreground">Lista de correos</h2>
            <p className="text-sm text-muted-foreground mt-1">
              No se envía nada extra al cliente. Confirmación de licencia y de tienda siguen yendo
              al comprador desde pedidos.{' '}
              <Link to="/store/orders" className="underline underline-offset-2">
                Pedidos tienda
              </Link>
              {' · '}
              <Link to="/licenses" className="underline underline-offset-2">
                Licencias
              </Link>
              .
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="py-8 flex items-center justify-center">
            <RefreshCw size={18} className="animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {recipients.length === 0 ? (
              <p className="text-sm text-muted-foreground">No hay destinatarios.</p>
            ) : (
              <ul className="divide-y divide-border rounded-lg border border-border">
                {recipients.map((item) => (
                  <li
                    key={item.email}
                    className="flex flex-col gap-2 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex items-center justify-between gap-2 sm:min-w-0">
                      <span className="truncate text-sm font-medium text-foreground">{item.email}</span>
                      <button
                        type="button"
                        className="rounded-full p-1 hover:bg-muted sm:hidden"
                        onClick={() => removeRecipient(item.email)}
                        disabled={!editable}
                        aria-label={`Quitar ${item.email}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      {OPS_EMAIL_CHANNELS.map((channel) => (
                        <label
                          key={channel}
                          className="inline-flex items-center gap-1.5 text-sm cursor-pointer text-foreground"
                        >
                          <input
                            type="checkbox"
                            checked={item[channel]}
                            onChange={(e) => toggleChannel(item.email, channel, e.target.checked)}
                            disabled={!editable}
                            className="rounded border-border"
                          />
                          {OPS_EMAIL_CHANNEL_LABELS[channel]}
                        </label>
                      ))}
                      <button
                        type="button"
                        className="hidden rounded-full p-1 hover:bg-muted sm:inline-flex"
                        onClick={() => removeRecipient(item.email)}
                        disabled={!editable}
                        aria-label={`Quitar ${item.email}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <form
              className="space-y-3 rounded-lg border border-dashed border-border p-3"
              onSubmit={(e) => {
                e.preventDefault();
                addRecipient();
              }}
            >
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => {
                    setNewEmail(e.target.value);
                    setFormError(null);
                  }}
                  placeholder="correo@dominio.com"
                  className="sm:flex-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  disabled={saveMutation.isPending}
                />
                <button
                  type="submit"
                  disabled={saveMutation.isPending}
                  className={cn(
                    'inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium',
                    'bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60'
                  )}
                >
                  {saveMutation.isPending ? 'Guardando…' : 'Añadir'}
                </button>
              </div>
              <div className="flex flex-wrap gap-3">
                {OPS_EMAIL_CHANNELS.map((channel) => (
                  <label
                    key={channel}
                    className="inline-flex items-center gap-1.5 text-sm cursor-pointer text-foreground"
                  >
                    <input
                      type="checkbox"
                      checked={newChannels[channel]}
                      onChange={(e) =>
                        setNewChannels((current) => ({
                          ...current,
                          [channel]: e.target.checked,
                        }))
                      }
                      disabled={saveMutation.isPending}
                      className="rounded border-border"
                    />
                    {OPS_EMAIL_CHANNEL_LABELS[channel]}
                  </label>
                ))}
              </div>
            </form>

            {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
            {saveMutation.isError ? (
              <p className="text-sm text-destructive">{(saveMutation.error as Error).message}</p>
            ) : null}
            {saveMutation.isSuccess ? (
              <p className="text-sm text-emerald-500">Lista actualizada.</p>
            ) : null}
            {isError ? (
              <p className="text-sm text-muted-foreground">
                No se pudo leer la lista. Se muestra la semilla (pedidos@).
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
